import React, { useState } from "react";
import { createPortal } from "react-dom";
import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { axiosClient } from "@/lib/axiosClient";
import {
    Search, Plus, Building2, User, Mail, Phone, Clock,
    CheckCircle2, XCircle, FileText, Pencil, Trash2,
    Loader2, Users, Receipt, ChevronLeft, ChevronRight, ListFilter,
    RefreshCw, ChevronDown, Eye,
    Hash
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import axios from "axios";

// --- TYPES ---
interface CollectiveBilling {
    id: number;
    title: string;
    institution_name: string;
    pic_name: string;
    pic_email: string;
    pic_phone: string;
    total_amount: string;
    invoice_number: string | null;
    invoice_title: string;
    currency: string;
    customer_number: string | null;
    payment_status: "unpaid" | "paid" | "expired" | "refunded";
    status: "draft" | "pending_approval" | "approved" | "rejected";
    description: string | null;
    json_payment?: Record<string, unknown> | string | null;
    created_at: string;
}

interface PaginatedResponse {
    current_page: number;
    last_page: number;
    total: number;
    per_page: number;
    from: number | null;
    to: number | null;
    data: CollectiveBilling[];
}

interface FormData {
    title: string;
    institution_name: string;
    pic_name: string;
    pic_email: string;
    pic_phone: string;
    description: string;
}

// ✅ Tambahan Type untuk Detail API
interface DetailMember {
    id: number;
    registration_id: number | null;
    participant_name: string;
    participant_type: string;
    is_presenter: boolean;
    individual_amount: string;
    conference_id: number;
    conference_name: string;
    package_name: string;
    is_valid: boolean;
}

interface DetailSummary {
    total_members: number;
    total_valid_members: number;
    total_invalid_members: number;
    payment_status: string;
    status: string;
}

interface BillingDetailResponse {
    id: number;
    title: string;
    institution_name: string;
    pic_name: string;
    pic_email: string;
    pic_phone: string;
    description: string | null;
    invoice_title: string;
    invoice_number: string;
    customer_number: string;
    currency: string;
    total_amount: string;
    payment_status: string;
    status: string;
    invoice_type: string;
    public_token: string;
    billing_url: string;
    created_at: string;
    updated_at: string;
    members: DetailMember[];
}

const initialFormData: FormData = {
    title: "", institution_name: "", pic_name: "", pic_email: "", pic_phone: "", description: ""
};

const formatRupiah = (val: string | number) => {
    return new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", minimumFractionDigits: 0 }).format(Number(val));
};

export default function CollectiveBillingPage() {
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    // States
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState("10");
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [paymentFilter, setPaymentFilter] = useState("all");

    // Modal & Expand States
    const [isFormModalOpen, setIsFormModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState<CollectiveBilling | null>(null);
    const [formData, setFormData] = useState<FormData>(initialFormData);
    const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

    // ✅ State Modal Detail
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    const [detailId, setDetailId] = useState<number | null>(null);

    // --- FETCH DATA LIST ---
    const { data: listData, isLoading, isFetching } = useQuery({
        queryKey: ["collective-billing", page, perPage, search, statusFilter, paymentFilter],
        queryFn: async (): Promise<PaginatedResponse> => {
            let url = `/private/collective-billing?page=${page}&limit=${perPage}`;
            if (search) url += `&search=${search}`;
            if (statusFilter !== "all") url += `&status=${statusFilter}`;
            if (paymentFilter !== "all") url += `&payment_status=${paymentFilter}`;
            const { data } = await axiosClient.get(url);
            return data.data;
        },
        placeholderData: keepPreviousData,
    });

    // --- FETCH DATA DETAIL (Aktif hanya jika detailId ada) ---
    const { data: detailApiData, isLoading: detailLoading } = useQuery({
        queryKey: ["collective-billing-detail", detailId],
        queryFn: async () => {
            const { data } = await axiosClient.get(`/private/collective-billing/detail/${detailId}`);
            return data as { status: string; invoice_type: string; summary: DetailSummary; data: BillingDetailResponse };
        },
        enabled: !!detailId,
    });

    // --- MUTATIONS ---
    const handleError = (error: unknown) => {
        if (axios.isAxiosError(error) && error.response?.data?.message) {
            toast.error(error.response.data.message);
        } else {
            toast.error("Terjadi kesalahan sistem.");
        }
    };

    const saveMutation = useMutation({
        mutationFn: async (payload: FormData) => {
            if (selectedItem) {
                return await axiosClient.put(`/private/collective-billing/${selectedItem.id}`, payload);
            }
            return await axiosClient.post(`/private/collective-billing`, payload);
        },
        onSuccess: (res) => {
            toast.success(res.data.message || "Data berhasil disimpan!");
            queryClient.invalidateQueries({ queryKey: ["collective-billing"] });
            closeModal();
        },
        onError: handleError
    });

    const deleteMutation = useMutation({
        mutationFn: async (id: number) => {
            return await axiosClient.delete(`/private/collective-billing/${id}`);
        },
        onSuccess: (res) => {
            toast.success(res.data.message || "Data berhasil dihapus!");
            queryClient.invalidateQueries({ queryKey: ["collective-billing"] });
            setIsDeleteModalOpen(false);
            setSelectedItem(null);
        },
        onError: handleError
    });

    // --- HANDLERS ---
    const openAddModal = () => {
        setSelectedItem(null);
        setFormData(initialFormData);
        setIsFormModalOpen(true);
    };

    const openEditModal = (item: CollectiveBilling) => {
        setSelectedItem(item);
        setFormData({
            title: item.title,
            institution_name: item.institution_name,
            pic_name: item.pic_name,
            pic_email: item.pic_email,
            pic_phone: item.pic_phone,
            description: item.description || ""
        });
        setIsFormModalOpen(true);
    };

    const closeModal = () => {
        setIsFormModalOpen(false);
        setSelectedItem(null);
        setFormData(initialFormData);
    };

    const toggleRow = (id: number) => {
        setExpandedRows(prev => {
            const newSet = new Set(prev);
            if (newSet.has(id)) newSet.delete(id);
            else newSet.add(id);
            return newSet;
        });
    };

    // ✅ Buka Modal Detail
    const openDetailModal = (id: number) => {
        setDetailId(id);
        setIsDetailModalOpen(true);
    };

    // --- RENDER HELPERS ---
    const getStatusBadge = (status: string) => {
        const badges: Record<string, JSX.Element> = {
            draft: <span className="bg-slate-100 text-slate-600 px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider border border-slate-200">Draft</span>,
            pending_approval: <span className="bg-amber-100 text-amber-700 px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider border border-amber-200">Menunggu</span>,
            approved: <span className="bg-emerald-100 text-emerald-700 px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider border border-emerald-200">Disetujui</span>,
            rejected: <span className="bg-rose-100 text-rose-700 px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider border border-rose-200">Ditolak</span>,
        };
        return badges[status] || badges.draft;
    };

    const getPaymentBadge = (status: string) => {
        const badges: Record<string, JSX.Element> = {
            paid: <span className="text-emerald-600 font-bold uppercase text-[10px] flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Lunas</span>,
            unpaid: <span className="text-amber-600 font-bold uppercase text-[10px] flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Belum Dibayar</span>,
            expired: <span className="text-rose-600 font-bold uppercase text-[10px] flex items-center gap-1"><XCircle className="w-3.5 h-3.5" /> Kedaluwarsa</span>,
            refunded: <span className="text-slate-500 font-bold uppercase text-[10px] flex items-center gap-1"><RefreshCw className="w-3.5 h-3.5" /> Dikembalikan</span>,
        };
        return badges[status] || badges.unpaid;
    };

    return (
        <div className="max-w-7xl mx-auto space-y-8 pb-20 px-4 md:px-0 animate-in fade-in duration-500">
            {/* --- HEADER --- */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white dark:bg-slate-900 p-6 md:p-8 rounded-[2rem] shadow-sm border border-slate-100 dark:border-slate-800">
                <div>
                    <div className="flex items-center gap-3 mb-2">
                        <div className="p-2.5 rounded-xl bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400">
                            <Building2 className="w-6 h-6" />
                        </div>
                        <h1 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">Kolektif Institusi</h1>
                    </div>
                    <p className="text-sm text-slate-500 dark:text-slate-400 max-w-xl">
                        Kelola tagihan grup/kolektif untuk pendaftaran institusi, dan atur anggota di dalamnya.
                    </p>
                </div>
                <Button onClick={openAddModal} className="h-12 px-6 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold shadow-md transition-all shrink-0">
                    <Plus className="w-5 h-5 mr-2" /> Tambah Kolektif
                </Button>
            </div>

            {/* --- TOOLBAR --- */}
            <div className="flex flex-col xl:flex-row gap-4 items-center justify-between bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm">
                <div className="relative w-full xl:w-1/3 group">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
                    <Input
                        placeholder="Cari judul, instansi, atau PIC..."
                        className="pl-10 h-12 bg-slate-50 dark:bg-slate-950 border-slate-200 rounded-xl"
                        value={search}
                        onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                    />
                </div>

                <div className="flex flex-wrap items-center gap-3 w-full xl:w-auto">
                    <Select value={statusFilter} onValueChange={(val) => { setStatusFilter(val); setPage(1); }}>
                        <SelectTrigger className="w-[150px] h-12 rounded-xl border-slate-200 text-xs bg-white dark:bg-slate-900">
                            <SelectValue placeholder="Status Data" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Semua Status</SelectItem>
                            <SelectItem value="draft">Draft</SelectItem>
                            <SelectItem value="approved">Disetujui</SelectItem>
                        </SelectContent>
                    </Select>

                    <Select value={paymentFilter} onValueChange={(val) => { setPaymentFilter(val); setPage(1); }}>
                        <SelectTrigger className="w-[150px] h-12 rounded-xl border-slate-200 text-xs bg-white dark:bg-slate-900">
                            <SelectValue placeholder="Status Bayar" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Semua Pembayaran</SelectItem>
                            <SelectItem value="paid">Lunas</SelectItem>
                            <SelectItem value="unpaid">Belum Dibayar</SelectItem>
                        </SelectContent>
                    </Select>

                    <Select value={perPage} onValueChange={(val) => { setPerPage(val); setPage(1); }}>
                        <SelectTrigger className="w-[110px] h-12 rounded-xl border-slate-200 text-xs bg-white dark:bg-slate-900">
                            <ListFilter size={14} className="mr-2 opacity-60" />
                            <SelectValue placeholder="Limit" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="10">10 Baris</SelectItem>
                            <SelectItem value="25">25 Baris</SelectItem>
                            <SelectItem value="50">50 Baris</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {/* --- TABLE --- */}
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-sm overflow-hidden relative">
                {isFetching && !isLoading && (
                    <div className="absolute inset-0 bg-white/50 dark:bg-slate-900/50 backdrop-blur-[1px] z-10 flex items-center justify-center">
                        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                    </div>
                )}

                <div className="overflow-x-auto pb-4">
                    <table className="w-full text-left text-sm whitespace-nowrap">
                        <thead className="bg-slate-50 dark:bg-slate-800/50 text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider text-[10px]">
                            <tr>
                                <th className="px-4 py-5 border-b border-slate-100 dark:border-slate-800 w-10 text-center"></th>
                                <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800">Judul & Instansi</th>
                                <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800">PIC Detail</th>
                                <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800">Tagihan & Status</th>
                                <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800 text-center sticky right-0 z-10 bg-slate-50 dark:bg-slate-800/90 shadow-[-10px_0_15px_-10px_rgba(0,0,0,0.1)]">Menu</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                            {isLoading ? (
                                [...Array(5)].map((_, i) => (
                                    <tr key={i}><td colSpan={5} className="h-20 animate-pulse bg-slate-50/30" /></tr>
                                ))
                            ) : !listData || listData.data.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="h-48 text-center text-slate-400">
                                        <Building2 className="w-12 h-12 mx-auto mb-3 opacity-20" />
                                        <p>Data master kolektif tidak ditemukan.</p>
                                    </td>
                                </tr>
                            ) : (
                                listData.data.map((item) => (
                                    <React.Fragment key={item.id}>
                                        <tr className="hover:bg-slate-50/50 dark:hover:bg-slate-800/20 transition-colors bg-white dark:bg-slate-900 group">
                                            <td className="px-4 py-4 text-center cursor-pointer" onClick={() => toggleRow(item.id)}>
                                                <button className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-md transition-colors text-slate-400">
                                                    {expandedRows.has(item.id) ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                                </button>
                                            </td>
                                            <td className="px-6 py-4 max-w-[300px]">
                                                <p className="font-bold text-slate-900 dark:text-white text-sm truncate" title={item.title}>{item.title}</p>
                                                <p className="text-[11px] font-medium text-slate-500 mt-1 flex items-center gap-1 truncate"><Building2 className="w-3.5 h-3.5" /> {item.institution_name}</p>
                                            </td>
                                            <td className="px-6 py-4">
                                                <p className="font-bold text-slate-800 dark:text-slate-200 text-xs flex items-center gap-1.5"><User className="w-3.5 h-3.5 text-slate-400" /> {item.pic_name}</p>
                                                <div className="flex flex-col mt-1.5 space-y-0.5">
                                                    <p className="text-[10px] text-slate-500 flex items-center gap-1.5"><Mail className="w-3 h-3 text-slate-400" /> {item.pic_email}</p>
                                                    <p className="text-[10px] text-slate-500 flex items-center gap-1.5"><Phone className="w-3 h-3 text-slate-400" /> {item.pic_phone}</p>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-3">
                                                    <div>
                                                        <p className="font-black text-sm text-slate-900 dark:text-white">{formatRupiah(item.total_amount)}</p>
                                                        <p className="text-[10px] font-mono text-slate-400 mt-0.5 flex items-center gap-1"><Receipt className="w-3 h-3" /> {item.invoice_number || 'Belum ada Invoice'}</p>
                                                    </div>
                                                    <div className="w-px h-8 bg-slate-200 dark:bg-slate-700 mx-2" />
                                                    <div className="flex flex-col gap-2">
                                                        {getStatusBadge(item.status)}
                                                        {getPaymentBadge(item.payment_status)}
                                                    </div>
                                                </div>
                                            </td>

                                            <td className="px-6 py-4 text-center sticky right-0 z-10 bg-white dark:bg-slate-900 group-hover:bg-slate-50/50 dark:group-hover:bg-slate-800/80 shadow-[-10px_0_15px_-10px_rgba(0,0,0,0.1)] transition-colors">
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger
                                                        className="h-8 px-3 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 font-semibold shadow-sm flex items-center gap-1.5 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-700 mx-auto outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        Aksi <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end" className="w-48 rounded-xl z-[9999] bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 shadow-xl">

                                                        {/* ✅ TOMBOL DETAIL DITAMBAHKAN DI SINI */}
                                                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openDetailModal(item.id); }} className="cursor-pointer py-2.5 hover:bg-slate-50 dark:hover:bg-slate-800">
                                                            <Eye className="w-4 h-4 mr-3 text-indigo-500" />
                                                            <span className="font-semibold text-slate-700 dark:text-slate-300">Detail Tagihan</span>
                                                        </DropdownMenuItem>
                                                        <DropdownMenuSeparator className="bg-slate-100 dark:bg-slate-800" />

                                                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/collective-billing/${item.id}`); }} className="cursor-pointer py-2.5 hover:bg-slate-50 dark:hover:bg-slate-800">
                                                            <Users className="w-4 h-4 mr-3 text-blue-500" />
                                                            <span className="font-semibold text-slate-700 dark:text-slate-300">Kelola Anggota</span>
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openEditModal(item); }} className="cursor-pointer py-2.5 hover:bg-slate-50 dark:hover:bg-slate-800">
                                                            <Pencil className="w-4 h-4 mr-3 text-amber-500" />
                                                            <span className="font-semibold text-slate-700 dark:text-slate-300">Edit Master</span>
                                                        </DropdownMenuItem>
                                                        <DropdownMenuSeparator className="bg-slate-100 dark:bg-slate-800" />
                                                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setSelectedItem(item); setIsDeleteModalOpen(true); }} className="cursor-pointer py-2.5 text-rose-600 focus:text-rose-700 focus:bg-rose-50 dark:focus:bg-rose-950/30">
                                                            <Trash2 className="w-4 h-4 mr-3" />
                                                            <span className="font-semibold">Hapus Kolektif</span>
                                                        </DropdownMenuItem>
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            </td>
                                        </tr>

                                        {expandedRows.has(item.id) && (
                                            <tr className="bg-slate-50/80 dark:bg-slate-800/30 border-b border-slate-200 dark:border-slate-800">
                                                <td colSpan={5} className="px-8 py-6 shadow-inner">
                                                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 animate-in fade-in slide-in-from-top-2 duration-300">
                                                        <div className="space-y-4">
                                                            <h5 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-1.5"><Receipt className="w-3.5 h-3.5" /> Detail Tagihan</h5>
                                                            <div className="grid grid-cols-2 gap-4">
                                                                <div>
                                                                    <p className="text-xs text-slate-500 mb-0.5">Mata Uang</p>
                                                                    <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">{item.currency || 'IDR'}</p>
                                                                </div>
                                                                <div>
                                                                    <p className="text-xs text-slate-500 mb-0.5">Virtual Account</p>
                                                                    <p className="text-sm font-semibold text-slate-800 dark:text-slate-200 font-mono">{item.invoice_number || '-'}</p>
                                                                </div>
                                                                <div className="col-span-2">
                                                                    <p className="text-xs text-slate-500 mb-0.5">Keterangan Invoice</p>
                                                                    <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">{item.invoice_title || '-'}</p>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div className="space-y-4">
                                                            <h5 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" /> Catatan / Deskripsi</h5>
                                                            {item.description ? (
                                                                <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed bg-white dark:bg-slate-900/50 p-4 rounded-xl border border-slate-200 dark:border-slate-700 max-h-[160px] overflow-y-auto whitespace-pre-wrap">
                                                                    {item.description}
                                                                </p>
                                                            ) : (
                                                                <div className="p-4 rounded-xl border border-slate-200 border-dashed dark:border-slate-700 flex items-center justify-center h-20 opacity-50">
                                                                    <span className="text-xs italic text-slate-500">Tidak ada catatan deskripsi.</span>
                                                                </div>
                                                            )}
                                                        </div>

                                                        <div className="space-y-3">
                                                            <h5 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-1.5"><Hash className="w-3.5 h-3.5" /> Response Gateway</h5>
                                                            {item.json_payment ? (
                                                                <div className="bg-[#0D1117] rounded-xl p-4 overflow-auto max-h-[160px] border border-slate-800">
                                                                    <pre className="text-[10px] text-green-400 font-mono leading-relaxed whitespace-pre-wrap break-all">
                                                                        {typeof item.json_payment === 'object' && item.json_payment !== null
                                                                            ? JSON.stringify(item.json_payment, null, 2)
                                                                            : String(item.json_payment)}
                                                                    </pre>
                                                                </div>
                                                            ) : (
                                                                <div className="p-4 rounded-xl border border-slate-200 border-dashed dark:border-slate-700 flex items-center justify-center h-20 opacity-50">
                                                                    <span className="text-xs italic text-slate-500">Belum ada response log pembayaran.</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* PAGINATION */}
                {listData && listData.total > 0 && (
                    <div className="p-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 flex justify-between items-center text-sm text-slate-500">
                        <span>Menampilkan {listData.from} - {listData.to} dari {listData.total}</span>
                        <div className="flex gap-2">
                            <Button variant="outline" size="sm" onClick={() => setPage(p => p - 1)} disabled={page === 1} className="h-9 px-3 rounded-lg"><ChevronLeft className="w-4 h-4 mr-1" /> Prev</Button>
                            <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={page === listData.last_page} className="h-9 px-3 rounded-lg">Next <ChevronRight className="w-4 h-4 ml-1" /></Button>
                        </div>
                    </div>
                )}
            </div>

            {/* ==================================================================== */}
            {/* ✅ MODAL DETAIL TAGIHAN KOLEKTIF DENGAN PORTAL */}
            {/* ==================================================================== */}
            {isDetailModalOpen && createPortal(
                <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in">
                    <div className="bg-white dark:bg-slate-900 rounded-[2rem] shadow-2xl max-w-4xl w-full flex flex-col max-h-[90vh] overflow-hidden animate-in zoom-in-95 border border-slate-200 dark:border-slate-700">
                        {/* Header Modal */}
                        <div className="p-6 md:px-8 border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50 flex justify-between items-center shrink-0">
                            <div>
                                <h3 className="font-black text-xl flex items-center gap-2 text-slate-900 dark:text-white">
                                    <Receipt className="w-6 h-6 text-indigo-600" /> Detail Tagihan Kolektif
                                </h3>
                                <p className="text-xs text-slate-500 mt-1 font-mono">{detailApiData?.data.invoice_number || "Draft Tagihan"}</p>
                            </div>
                            <button onClick={() => setIsDetailModalOpen(false)} className="p-2.5 bg-slate-200/50 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 rounded-full transition-colors">
                                <XCircle className="w-5 h-5 text-slate-500 dark:text-slate-400" />
                            </button>
                        </div>

                        {/* Body Modal */}
                        <div className="p-6 md:p-8 overflow-y-auto space-y-8 bg-white dark:bg-slate-900">
                            {detailLoading ? (
                                <div className="py-20 flex flex-col items-center justify-center">
                                    <Loader2 className="w-10 h-10 animate-spin text-indigo-600 mb-4" />
                                    <span className="text-slate-500 text-sm font-medium">Memuat data detail...</span>
                                </div>
                            ) : detailApiData ? (
                                <>
                                    {/* 1. Summary Cards */}
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                        <div className="bg-indigo-50 dark:bg-indigo-900/20 p-4 rounded-xl border border-indigo-100 dark:border-indigo-800/50">
                                            <p className="text-[10px] font-bold text-indigo-600 dark:text-indigo-400 uppercase tracking-widest mb-1">Total Member</p>
                                            <p className="text-2xl font-black text-indigo-900 dark:text-indigo-100">{detailApiData.summary.total_members}</p>
                                        </div>
                                        <div className="bg-emerald-50 dark:bg-emerald-900/20 p-4 rounded-xl border border-emerald-100 dark:border-emerald-800/50">
                                            <p className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-widest mb-1">Valid Member</p>
                                            <p className="text-2xl font-black text-emerald-900 dark:text-emerald-100">{detailApiData.summary.total_valid_members}</p>
                                        </div>
                                        <div className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-xl border border-slate-100 dark:border-slate-700">
                                            <p className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-1">Status Verifikasi</p>
                                            <div className="mt-1.5">{getStatusBadge(detailApiData.summary.status)}</div>
                                        </div>
                                        <div className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-xl border border-slate-100 dark:border-slate-700">
                                            <p className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-1">Pembayaran</p>
                                            <div className="mt-1.5">{getPaymentBadge(detailApiData.summary.payment_status)}</div>
                                        </div>
                                    </div>

                                    {/* 2. Info Institusi & Total Tagihan */}
                                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center p-6 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-2xl gap-6">
                                        <div>
                                            <h4 className="font-bold text-slate-900 dark:text-white mb-1">{detailApiData.data.title}</h4>
                                            <p className="text-xs text-slate-500 mb-3 flex items-center gap-1"><Building2 className="w-3.5 h-3.5" /> {detailApiData.data.institution_name}</p>
                                            <div className="text-xs text-slate-600 dark:text-slate-400 space-y-1">
                                                <p className="flex items-center gap-2"><User className="w-3.5 h-3.5 text-slate-400" /> {detailApiData.data.pic_name}</p>
                                                <p className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-slate-400" /> {detailApiData.data.pic_email}</p>
                                            </div>
                                        </div>
                                        <div className="text-left md:text-right bg-white dark:bg-slate-900 p-4 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm w-full md:w-auto">
                                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Total Tagihan (IDR)</p>
                                            <p className="text-3xl font-black text-blue-600 dark:text-blue-400">{formatRupiah(detailApiData.data.total_amount)}</p>
                                            <a
                                                href={detailApiData.data.billing_url}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="inline-flex mt-3 text-xs font-bold text-indigo-600 hover:text-indigo-700 hover:underline transition-colors"
                                            >
                                                Buka Halaman Pembayaran &rarr;
                                            </a>
                                        </div>
                                    </div>

                                    {/* 3. Tabel Detail Member */}
                                    <div className="space-y-3">
                                        <h4 className="font-bold text-sm text-slate-700 dark:text-slate-300 flex items-center gap-2">
                                            <Users className="w-4 h-4 text-slate-400" /> Rincian Anggota Tagihan
                                        </h4>
                                        <div className="border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
                                            <table className="w-full text-left text-xs whitespace-nowrap">
                                                <thead className="bg-slate-100 dark:bg-slate-800">
                                                    <tr>
                                                        <th className="px-4 py-3 font-bold text-slate-600 dark:text-slate-300">Nama Partisipan</th>
                                                        <th className="px-4 py-3 font-bold text-slate-600 dark:text-slate-300">Konferensi & Paket</th>
                                                        <th className="px-4 py-3 font-bold text-slate-600 dark:text-slate-300 text-center">Status</th>
                                                        <th className="px-4 py-3 font-bold text-slate-600 dark:text-slate-300 text-right">Nominal</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                                    {detailApiData.data.members.map((member) => (
                                                        <tr key={member.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                                                            <td className="px-4 py-3">
                                                                <p className="font-bold text-slate-900 dark:text-white">{member.participant_name}</p>
                                                                <p className="text-[10px] text-slate-500 mt-0.5">{member.is_presenter ? 'Presenter' : 'Non-Presenter'} ({member.participant_type})</p>
                                                            </td>
                                                            <td className="px-4 py-3 max-w-[200px] truncate">
                                                                <p className="font-semibold text-slate-700 dark:text-slate-300 truncate">{member.conference_name}</p>
                                                                <p className="text-[10px] text-slate-500 truncate mt-0.5">{member.package_name}</p>
                                                            </td>
                                                            <td className="px-4 py-3 text-center">
                                                                {member.is_valid ? (
                                                                    <span className="inline-flex bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded text-[10px] font-bold">Valid</span>
                                                                ) : (
                                                                    <span className="inline-flex bg-rose-100 text-rose-700 px-2 py-0.5 rounded text-[10px] font-bold">Invalid</span>
                                                                )}
                                                            </td>
                                                            <td className="px-4 py-3 text-right font-black text-slate-900 dark:text-white">
                                                                {formatRupiah(member.individual_amount)}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </>
                            ) : null}
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* --- MODAL FORM (CREATE / EDIT) --- */}
            {isFormModalOpen && createPortal(
                <div className="fixed inset-0 z-[999] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in">
                    <div className="bg-white dark:bg-slate-900 rounded-[2rem] shadow-2xl max-w-xl w-full flex flex-col overflow-hidden animate-in zoom-in-95">
                        <div className="p-6 border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50 flex justify-between items-center">
                            <h3 className="font-black text-xl flex items-center gap-2 text-slate-900 dark:text-white">
                                <FileText className="w-5 h-5 text-blue-600" /> {selectedItem ? "Edit Data Kolektif" : "Buat Master Kolektif"}
                            </h3>
                            <button onClick={closeModal} className="p-2 bg-slate-200/50 hover:bg-slate-200 rounded-full transition-colors"><XCircle className="w-5 h-5 text-slate-500" /></button>
                        </div>
                        <form onSubmit={(e) => { e.preventDefault(); saveMutation.mutate(formData); }} className="p-6 space-y-4">
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Judul Rombongan Kolektif</label>
                                <Input required placeholder="Contoh: Rombongan Fakultas Kedokteran Gelombang 1" value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })} className="h-12 rounded-xl" />
                            </div>
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Nama Institusi Asal</label>
                                <Input required placeholder="Contoh: Universitas Gadjah Mada" value={formData.institution_name} onChange={e => setFormData({ ...formData, institution_name: e.target.value })} className="h-12 rounded-xl" />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Nama PIC</label>
                                    <Input required placeholder="Nama Lengkap Penanggung Jawab" value={formData.pic_name} onChange={e => setFormData({ ...formData, pic_name: e.target.value })} className="h-12 rounded-xl" />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">No. Telepon / WhatsApp</label>
                                    <Input required placeholder="0812345..." value={formData.pic_phone} onChange={e => setFormData({ ...formData, pic_phone: e.target.value })} className="h-12 rounded-xl" />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Email PIC</label>
                                <Input required type="email" placeholder="email@institusi.ac.id" value={formData.pic_email} onChange={e => setFormData({ ...formData, pic_email: e.target.value })} className="h-12 rounded-xl" />
                            </div>
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Catatan / Deskripsi (Opsional)</label>
                                <textarea placeholder="Informasi tambahan..." value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} className="w-full min-h-[80px] p-3 text-sm rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 focus:ring-2 focus:ring-blue-500 outline-none" />
                            </div>
                            <div className="pt-4 flex justify-end gap-3 border-t border-slate-100 dark:border-slate-800 mt-6">
                                <Button type="button" variant="ghost" onClick={closeModal} className="h-12 px-6 rounded-xl font-bold">Batal</Button>
                                <Button type="submit" disabled={saveMutation.isPending} className="h-12 px-8 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold transition-all active:scale-95">
                                    {saveMutation.isPending ? <Loader2 className="w-5 h-5 animate-spin" /> : "Simpan Data"}
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>,
                document.body
            )}

            {/* --- MODAL DELETE DENGAN PORTAL --- */}
            {isDeleteModalOpen && createPortal(
                <div className="fixed inset-0 z-[999] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in">
                    <div className="bg-white dark:bg-slate-900 rounded-[2rem] shadow-2xl max-w-sm w-full p-8 text-center animate-in zoom-in-95">
                        <div className="w-16 h-16 bg-rose-100 dark:bg-rose-900/30 text-rose-600 rounded-full flex items-center justify-center mx-auto mb-4"><Trash2 className="w-8 h-8" /></div>
                        <h3 className="text-2xl font-black text-slate-900 dark:text-white mb-2">Hapus Master Kolektif?</h3>
                        <p className="text-slate-500 text-sm mb-8 leading-relaxed">Anda yakin ingin menghapus data rombongan <strong>{selectedItem?.title}</strong>? Seluruh data anggota di dalamnya juga akan terhapus secara permanen.</p>
                        <div className="flex gap-3">
                            <Button variant="outline" onClick={() => setIsDeleteModalOpen(false)} className="flex-1 h-12 rounded-xl font-bold">Batal</Button>
                            <Button onClick={() => selectedItem && deleteMutation.mutate(selectedItem.id)} disabled={deleteMutation.isPending} className="flex-1 h-12 rounded-xl bg-rose-600 hover:bg-rose-700 text-white font-bold transition-all active:scale-95">
                                {deleteMutation.isPending ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : "Ya, Hapus Data"}
                            </Button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
}